import { z } from 'zod';
import { config } from '../config';
import type { DiscoveryBucket, HypothesisPhase, TemplateRow } from '../types';
import { dispatch } from './bus';
import type { DispatchResult } from './bus';
import seedFile from '../seeds/builtinTemplates.json';

/**
 * PRE-2 — the seeded built-in template library: **parse, validate, install**.
 *
 * > *"Seeded built-in template library grouped by the four moments plus GK and individual stubs;
 * > the app ships with ≥ 10 hypotheses per moment bucket. Seed content is data (JSON), not code."*
 *
 * The data is `src/seeds/builtinTemplates.json`. This module is the boundary it crosses, and it is
 * a real boundary: design §6.0 requires *"A JSON-schema validation of the seed file … so a
 * malformed or under-filled bucket fails the build rather than shipping a thin library."*
 *
 * ── Where the validation actually bites ──────────────────────────────────────
 *
 * The parse runs **at import time**, on `config/validate.ts`'s precedent. Three consequences, and
 * all three are wanted:
 *
 * 1. `seedTemplates.test.ts` imports this module, so a malformed seed fails the test suite — the
 *    thing this project runs as CI.
 * 2. Every other test that touches the Pre-Match flow imports it transitively, so the failure is
 *    loud and immediate rather than a single red assertion in one file.
 * 3. A seed that somehow shipped broken throws at app start rather than rendering an empty library
 *    at a venue, where a thin library is indistinguishable from "I forgot to pick anything".
 *
 * The count rules live in the schema rather than only in the test, for the same reason: a rule
 * enforced only by a test is a rule that a second seed file added later would not be checked
 * against.
 */

// ─────────────────────────────────────────────────────────────────────────────
// The schema
// ─────────────────────────────────────────────────────────────────────────────

const MOMENT_BUCKETS = [
  'inPossession',
  'outOfPossession',
  'attTransition',
  'defTransition',
] as const satisfies readonly DiscoveryBucket[];

const HYPOTHESIS_PHASES = [
  ...MOMENT_BUCKETS,
  'setPiece',
  'individual',
  'gk',
] as const satisfies readonly HypothesisPhase[];

const SlotSchema = z.object({
  key: z.string().min(1),
  kind: z.enum(['shirtNumber', 'foot']),
});

const EntrySchema = z
  .object({
    text: z.string().min(1),
    phase: z.enum(HYPOTHESIS_PHASES),
    /**
     * §3.2's source **1**: *"Explicit, from seed or template data. Every `TemplateEntry` in the
     * PRE-2 seed JSON carries `discoveryBucket`. … CI validates that every seed entry has one."*
     * Non-optional here is that validation — DISC-2 has four pages and `phase` has seven values, so
     * an entry without a bucket would be an untappable hypothesis.
     */
    discoveryBucket: z.enum(MOMENT_BUCKETS),
    slots: z.array(SlotSchema).optional(),
  })
  .superRefine((entry, ctx) => {
    for (const slot of entry.slots ?? []) {
      if (!entry.text.includes(`{${slot.key}}`)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `PRE-4: slot "${slot.key}" has no {${slot.key}} placeholder in "${entry.text}"`,
        });
      }
    }
    for (const kind of ['shirtNumber', 'foot'] as const) {
      // Mirrors `domain/setup.resolveTemplateText`'s ERR_SLOT_AMBIGUOUS rejection. A stub naming two
      // shirt numbers has no single `Player` to bind to (FILE-2), so it can never be resolved — and
      // a stub that can never be resolved is a dead row in the library, not a runtime error.
      if ((entry.slots ?? []).filter((slot) => slot.kind === kind).length > 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `PRE-4: "${entry.text}" declares more than one ${kind} slot, so it can bind to no single player`,
        });
      }
    }
  });

const GroupSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  phase: z.enum(HYPOTHESIS_PHASES).nullable(),
  hypothesisTexts: z.array(EntrySchema).min(1),
});

const SeedFileSchema = z
  .object({
    $comment: z.string().optional(),
    version: z.literal(1),
    templates: z.array(GroupSchema).min(1),
  })
  .superRefine((file, ctx) => {
    const entries = file.templates.flatMap((group) => group.hypothesisTexts);

    // PRE-2 — "≥ 10 hypotheses per moment bucket". Counted on the entry's own `phase`, not on
    // `discoveryBucket`: GK and individual entries are *routed* to a moment page by §3.2, and
    // counting them there would let a thin `attTransition` group hide behind seven keeper stubs.
    for (const moment of MOMENT_BUCKETS) {
      const count = entries.filter((entry) => entry.phase === moment).length;
      if (count < config.MIN_SEEDS_PER_BUCKET) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `PRE-2: the "${moment}" bucket ships ${String(count)} hypotheses; at least ${String(config.MIN_SEEDS_PER_BUCKET)} are required`,
        });
      }
    }

    // PRE-2 — "plus GK and individual stubs". Both groups are named in the requirement, and the
    // individual group is the only source of PRE-4's fill-in-the-blank rows.
    if (!entries.some((entry) => entry.phase === 'gk')) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'PRE-2: no GK hypotheses are seeded' });
    }
    if (!entries.some((entry) => entry.phase === 'individual' && (entry.slots ?? []).length > 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'PRE-4: no individual fill-in-the-blank stubs are seeded',
      });
    }

    const ids = file.templates.map((group) => group.id);
    if (new Set(ids).size !== ids.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Two seeded template groups share an id; installation is keyed on it',
      });
    }
  });

export class SeedValidationError extends Error {
  override readonly name = 'SeedValidationError';
  constructor(readonly issues: readonly string[]) {
    super(`The seeded template library is invalid:\n  - ${issues.join('\n  - ')}`);
  }
}

/**
 * Parses one seed file into installable rows. Exported so a test can feed it a *bad* file — the
 * only way to know the validation above is doing anything.
 *
 * @throws {SeedValidationError} listing every problem, not just the first.
 */
export function parseSeedFile(raw: unknown): TemplateRow[] {
  const parsed = SeedFileSchema.safeParse(raw);
  if (!parsed.success) {
    throw new SeedValidationError(
      parsed.error.issues.map((issue) => `${issue.path.join('.') || '(seed)'}: ${issue.message}`),
    );
  }

  return parsed.data.templates.map((group) => ({
    id: group.id,
    name: group.name,
    phase: group.phase,
    hypothesisTexts: group.hypothesisTexts.map((entry) => ({
      text: entry.text,
      phase: entry.phase,
      discoveryBucket: entry.discoveryBucket,
      ...(entry.slots === undefined ? {} : { slots: entry.slots }),
    })),
    // PRE-2 — "loaded at first run into `Template` rows with `builtin = true`" (§6.0). PRE-6's
    // user-authored templates (U-16) are the `false` half of this flag.
    builtin: true,
  }));
}

/** The shipped library, validated at import. */
export const BUILTIN_TEMPLATES: readonly TemplateRow[] = parseSeedFile(seedFile);

/**
 * Installs any built-in group the profile does not already hold.
 *
 * Idempotent, and through `dispatch` rather than a direct write — see `InstallSeedTemplatesCommand`
 * for why (the §13.7 writer lock is the reason, and it is not a stylistic one).
 */
export function installSeedTemplates(): Promise<DispatchResult> {
  return dispatch({ type: 'InstallSeedTemplates', templates: BUILTIN_TEMPLATES });
}
